import React, { useState, useEffect, ChangeEvent } from 'react';
import { Table, Button, Input, Form, message, Space, Row, Col, Modal } from 'antd';
import apiClient from '../services/api';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { fetchWithLoading } from '../utils/fetchHelper';
import { confirmDelete } from '../utils/confirmDelete';
import MaintenanceTypeForm from '../components/MaintenanceTypeForm';
import { accentInsensitiveIncludes } from '../utils/accentUtils';

interface MaintenanceType {
  id: number;
  name: string;
  description?: string;
}

const MaintenanceTypePage = () => {
  const [form] = Form.useForm();
  const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingType, setEditingType] = useState<MaintenanceType | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [modalVisible, setModalVisible] = useState(false);

  const fetchTypes = async () => {
    await fetchWithLoading(
      () => apiClient.getMaintenanceTypes(),
      setLoading,
      (data) => setMaintenanceTypes(data as MaintenanceType[]),
      'Không thể tải danh sách loại bảo trì'
    );
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (editingType) {
        await apiClient.updateMaintenanceType(editingType.id, values);
        message.success('Cập nhật loại bảo trì thành công!');
      } else {
        await apiClient.createMaintenanceType(values);
        message.success('Tạo loại bảo trì thành công!');
      }
      form.resetFields();
      setEditingType(null);
      setModalVisible(false);
      fetchTypes();
    } catch (err: any) {
      const msg = err?.response?.data?.message || (editingType ? 'Cập nhật thất bại!' : 'Tạo loại bảo trì thất bại!');
      message.error(msg);
    }
  };

  const handleEdit = (record: MaintenanceType) => {
    setEditingType(record);
    form.setFieldsValue({ name: record.name, description: record.description });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteMaintenanceType(id);
      message.success('Đã xóa loại bảo trì');
      fetchTypes();
    } catch {
      message.error('Xóa loại bảo trì thất bại!');
    }
  };

  const handleDeleteClick = (record: MaintenanceType) => {
    confirmDelete('Bạn có chắc muốn xóa loại bảo trì này?', () => handleDelete(record.id));
  };

  const filteredMaintenanceTypes = maintenanceTypes.filter((t) =>
    accentInsensitiveIncludes(t.name, searchValue) ||
    accentInsensitiveIncludes(t.description || '', searchValue)
  );
  const columns = [
    { title: 'Tên loại bảo trì', dataIndex: 'name', key: 'name' },
    { title: 'Mô tả', dataIndex: 'description', key: 'description' },
    {
      title: 'Hành động', key: 'action', width: 110,
      render: (_: any, record: MaintenanceType) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteClick(record)} />
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col xs={24} md={20}>
          <Input
            className="ams-input"
            placeholder="Tìm kiếm loại bảo trì"
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            allowClear
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button className="ams-btn" type="primary" size="large" icon={<EditOutlined />} style={{ borderRadius: 8, height: 40, width: '100%' }} onClick={() => { setEditingType(null); form.resetFields(); setModalVisible(true); }}>Thêm mới</Button>
        </Col>
      </Row>
      <Modal
        open={modalVisible}
        title={editingType ? 'Cập nhật loại bảo trì' : 'Tạo mới loại bảo trì'}
        onCancel={() => { setModalVisible(false); setEditingType(null); form.resetFields(); }}
        footer={null}
      >
        <MaintenanceTypeForm
          form={form}
          initialValues={editingType || {}}
          onFinish={handleSubmit}
          loading={loading}
          submitText={editingType ? 'Cập nhật' : 'Tạo mới'}
        />
        {editingType && (
          <Button
            className="ams-btn"
            style={{ width: '100%', borderRadius: 8, height: 40, marginTop: 8 }}
            size="large"
            onClick={() => {
              setEditingType(null);
              form.resetFields();
              setModalVisible(false);
            }}
          >
            Hủy
          </Button>
        )}
      </Modal>
      <Table
        dataSource={filteredMaintenanceTypes}
        rowKey="id"
        loading={loading}
        columns={columns}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default MaintenanceTypePage; 